import Phaser from "phaser";
import { BigNumber } from "@yjsgames/bignumber";
import { formatBig } from "../ui/formatNumber.js";
import { strengthToHsv } from "./brickStrength.js";
import { crackStageFor, crackTextureFor, MYSTERY_CHEST_ICON_KEY, pickBrickTexture, type BrickTextureSpec } from "./brickAppearance.js";
import { pickShatterVariant } from "./brickShatter.js";
import { DEPTH } from "./depthLayers.js";
import type { BrickCellKind } from "../data/ChapterData.js";
import {
  type BrickAnchorsY,
  brickLabelRestY,
  brickGlyphRestY,
  resolveBrickLabelText,
  resolveShieldBadgeLayout,
  shiftBrickAnchorsY,
  translateBrickAnchorsY,
  deriveContentBaseY,
  resolveAnchorsAtOffset,
  contentOffsetMismatch,
} from "./brickLayout.js";
import type { WorldBrickTextures } from "../data/worldData.js";
import { FORCE_LOW_SPEC_RENDER_ENABLED } from "../env/appFlavor.js";
import {
  CIRCUIT_LABEL_MIN_SCALE,
  type BrickVisualTextureSpec,
} from "./brickVisualTextures.js";
import { BrickVisualParts } from "./brickVisualParts.js";
import { BRICK_DETAIL_COLOR, drawBrickKindDetail } from "./brickKindDetailDrawing.js";
import { drawBrickSparkle } from "./brickSparkleDrawing.js";
import {
  PRESSURE_DANGER_ALPHA,
  PRESSURE_DANGER_BLINK_MS,
  PRESSURE_DANGER_MIN_ALPHA,
  drawBrickPressureDanger,
} from "./brickPressureDangerDrawing.js";
import { applyBrickLabelTextStyle } from "./brickLabelPresentation.js";
import { drawBrickHealthBar, shouldShowBrickHpLabel } from "./brickHpPresentation.js";
import { resolveBrickSpawnVisualState } from "./brickVisualRuntime.js";
export { brickLabelRestY, brickGlyphRestY, shiftBrickAnchorsY } from "./brickLayout.js";

export interface BrickConfig {
  readonly hp: BigNumber;
  readonly rewardGold: BigNumber;
  readonly gridX: number;
  readonly gridY: number;
  /** 블록 픽셀 폭(멀티셀이면 가변). */
  readonly width: number;
  /** 블록 픽셀 높이(멀티셀이면 가변). */
  readonly height: number;
  /** 블록이 가로로 차지하는 전장 셀 수. 멀티셀 시각 세그먼트 표시에 사용한다. */
  readonly widthCells?: number;
  /** 블록이 세로로 차지하는 전장 셀 수. 멀티셀 시각 세그먼트 표시에 사용한다. */
  readonly heightCells?: number;
  /** 0=약~1=강(칸 수에서 파생). 강도 무지개색(빨강→보라) + 음계 비프가 이 값에서 파생 (brickStrength). */
  readonly strength: number;
  /** 벽돌 종류 — base/overlay 텍스처 키 + tint 모드를 결정 (brickAppearance). */
  readonly kind: BrickCellKind;
  /** 현재 월드의 벽돌 base/overlay 텍스처 세트. 미지정이면 기본 테마 벽돌 키를 사용한다. */
  readonly worldTextures?: WorldBrickTextures;
  readonly showHpLabel?: boolean;
  /** 멜로디 스테이지 셀의 음정 id(notes.tsv). 일반 셀은 미설정 — GameScene이 파괴 시 재생 음 결정에 참조. */
  readonly noteId?: string;
  /** 균열/파괴 조각 variant 시드 salt(스테이지·던전 인덱스 등) — 같은 칸도 판마다 패턴 다양화. 미지정 0. */
  readonly shatterSalt?: number;
  /** 방어막(shield) 타격 횟수 — shield 벽돌만. 미지정 0(일반). */
  readonly shieldHits?: number;
  /** 고정 방어력(armor) — armor 벽돌만, 챕터 비례 BigNumber. 미지정 0(일반). */
  readonly armor?: BigNumber;
  /** 재생(regen) 회복 주기(ms) — regen 벽돌만. 미지정 0(일반). */
  readonly healIntervalMs?: number;
  /** 재생 회복량 비율(대상 maxHp 기준) — regen 벽돌만. 미지정 0. */
  readonly healAmountRatio?: number;
  /** 재생 회복 반경(Chebyshev 그리드 거리) — regen 벽돌만. 미지정 0. */
  readonly healRadius?: number;
  /**
   * 생성 시점에 좌표(y)에 이미 반영돼 있는 게임뷰 콘텐츠 상향 오프셋(px). 펼침 상태에서 생성되면 0이 아니다.
   * 이 값으로 "offset 0 기준 base"를 역산해 reconcile(절대 재배치)의 기준으로 삼는다. 미지정 0(접힘).
   */
  readonly contentOffset?: number;
  /** 현재 선택 우주선에 따른 적 시각 스케일. 중심 좌표/HP는 유지하고 표시 크기만 조정한다. */
  readonly enemyScale?: number;
}

/**
 * 강도(strength)에 따라 무지개 색조(약함=빨강 → 강함=보라) + 남은 HP 비율로 채도/명도를 조정한다.
 * 색·음이 같은 strength에서 파생되도록 매핑은 brickStrength(순수)에 둔다.
 */
function strengthTint(strength: number, hp: BigNumber, maxHp: BigNumber): number {
  const hpRatio = maxHp.isZero() ? 0 : hp.div(maxHp).toNumber();
  const { h, s, v } = strengthToHsv(strength, hpRatio);
  const c = Phaser.Display.Color.HSVToRGB(h, s, v) as Phaser.Types.Display.ColorObject;
  return Phaser.Display.Color.GetColor(c.r, c.g, c.b);
}

/**
 * 텍스트를 maxW×maxH 박스 안에 들어오게 균일 축소(확대는 안 함) — 적용된 scale 반환.
 * BigNumber HP가 자릿수↑로 길어져도 블록 밖으로 삐지지 않게(autosize). origin 0.5 기준 중앙 정렬 유지.
 */
function fitText(t: Phaser.GameObjects.Text, maxW: number, maxH: number): number {
  t.setScale(1);
  const s = Math.min(1, maxW / Math.max(1, t.width), maxH / Math.max(1, t.height));
  t.setScale(s);
  return s;
}

function normalizeVisualScale(scale: number | undefined): number {
  return Number.isFinite(scale) && scale !== undefined && scale > 0 ? scale : 1;
}

const PRESSURE_DROP_DURATION_MS = 260;
const PRESSURE_DROP_JITTER_PX = 2;
const ENEMY_SCALE_TWEEN_MS = 180;

/** 벽돌 물리 프록시. 실제 시각 파트는 BrickVisualParts가 소유하고, 보상 지급/카운트는 GameScene이 담당한다. */
/** 풀 신규 인스턴스의 초기 텍스처 — configure가 kind별 실 텍스처로 교체. 존재하는 키여야 한다(부팅 후 생성). */
const DEFAULT_BRICK_TEX = pickBrickTexture("normal");

export class Brick extends Phaser.Physics.Arcade.Sprite {
  // ── per-spawn 상태(풀 재사용마다 configure가 전부 갱신) ──
  hp: BigNumber = BigNumber.ZERO;
  maxHp: BigNumber = BigNumber.ZERO;
  rewardGold: BigNumber = BigNumber.ZERO;
  gridX = 0;
  gridY = 0;
  /** 0=약~1=강. GameScene이 강도별 음계 비프(playBrickNote)에 사용. */
  strength = 0;
  /** 같은 블록 연타 콤보 — 마지막 타격 시각(scene.time.now ms). brickCombo가 windowMs로 콤보 판정. */
  lastHitMs = 0;
  /** 같은 블록 연타 콤보 카운트 — windowMs 이내 재타격마다 누적. 음계 비프 음정 상승에 사용. */
  comboCount = 0;
  /** 벽돌 종류 — 외형/보상 분기. */
  kind: BrickCellKind = "normal";
  /** 방어막 남은 타격 횟수 — shield 벽돌만 >0. 0이면 일반 HP 피해(brickDamage.resolveBrickDamage). */
  shieldHits = 0;
  /** 방어막 초기 타격 횟수(뱃지 표시·리셋용). */
  maxShieldHits = 0;
  /** 고정 방어력 — armor 벽돌만 >0(챕터 비례 BigNumber). damageBrick이 max(1, dmg−armor) 적용. */
  armor: BigNumber = BigNumber.ZERO;
  /** 재생 회복 주기(ms) — regen 벽돌만 >0. */
  healIntervalMs = 0;
  /** 재생 회복량 비율 — regen 벽돌만 >0. */
  healAmountRatio = 0;
  /** 재생 회복 반경 — regen 벽돌만 >0. */
  healRadius = 0;
  /** 마지막 회복 발동 시각(scene.time.now ms) — regen 벽돌만. update 틱이 interval 경과 판정에 사용. */
  lastHealMs = 0;
  /** 멜로디 스테이지 음정 id (없으면 일반 셀). */
  noteId?: string;
  /** 균열 패턴/파괴 조각 variant — (gridX, gridY, shatterSalt) 결정론 (brickShatter). */
  shatterVariant = 0;
  private tex: BrickTextureSpec = DEFAULT_BRICK_TEX;
  private visualTex: BrickVisualTextureSpec = {
    base: DEFAULT_BRICK_TEX.base,
    overlay: DEFAULT_BRICK_TEX.overlay,
  };
  private compositeVisualTexture: string | undefined;
  private readonly visuals: BrickVisualParts;
  /** 균열 오버레이 — 남은 HP 비율(crackStageFor)에 따라 variant 패턴 손상 라인 표시 ("HP 블록" 가독). */
  private crackStage: 0 | 1 | 2 | 3 | 4 = 0;
  private restX = 0;
  private widthCells = 1;
  private heightCells = 1;
  private dimGoldVisual = false;
  private isCircuitVisual = false;
  private visualDisplayWidth = 1;
  private visualDisplayHeight = 1;
  /** 피격 shake 복귀 기준 y. shiftContentOffset(펼치기 오프셋)이 시각 y와 함께 옮긴다. */
  private restY = 0;
  /** HP 라벨 rest y — 모든 벽돌에서 중앙. */
  private labelRestY = 0;
  /** 레거시 미스터리 "?" 글리프 rest y. */
  private glyphRestY = 0;
  /**
   * offset 0(접힘) 기준 절대 base 앵커 — configure 시 고정. reconcile이 이 base에서 `y = base - offset`로
   * 멱등 재배치한다. restY 등은 offset 반영값이라 base 복원에 쓸 수 없어 별도 보관.
   */
  private contentBaseY: BrickAnchorsY = { y: 0, restY: 0, labelRestY: 0, glyphRestY: 0 };
  /** HP 라벨 autosize fit 박스(블록 안). */
  private labelMaxW = 0;
  private labelMaxH = 0;
  private labelRestX = 0;
  private shieldIconRestX = 0;
  private shieldIconSize = 0;
  private showHpLabel = true;
  private sparklePhase = 0;
  private lastSparkleDrawMs = Number.NEGATIVE_INFINITY;
  /** HP 라벨 fit 적용 scale — 피해 펀치는 이 scale에 곱(블록 밖 삐짐 방지). */
  private labelBaseScale = 1;
  private pressureDropTweenTarget?: { y: number };
  private visualScaleTweenTarget?: {
    width: number;
    height: number;
    visualWidth: number;
    visualHeight: number;
  };
  private pressureDropTweenToken = 0;
  private pressureDangerTarget = false;
  private enemyVisualScale = 1;
  private unscaledDisplayWidth = 1;
  private unscaledDisplayHeight = 1;
  private unscaledVisualDisplayWidth = 1;
  private unscaledVisualDisplayHeight = 1;
  private readonly clearMarkStatus = (): void => this.markStatus.clear();
  private get glow(): Phaser.GameObjects.Graphics { return this.visuals.glow; }
  private get glowImage(): Phaser.GameObjects.Image { return this.visuals.glowImage; }
  private get pressureDanger(): Phaser.GameObjects.Graphics { return this.visuals.pressureDanger; }
  private get baseFrame(): Phaser.GameObjects.Image { return this.visuals.baseFrame; }
  private get overlay(): Phaser.GameObjects.Image { return this.visuals.overlay; }
  private get kindDetail(): Phaser.GameObjects.Graphics { return this.visuals.kindDetail; }
  private get mysteryIcon(): Phaser.GameObjects.Image { return this.visuals.mysteryIcon; }
  private get sparkle(): Phaser.GameObjects.Graphics { return this.visuals.sparkle; }
  private get markStatus() { return this.visuals.markStatus; }
  private get crack(): Phaser.GameObjects.Image { return this.visuals.crack; }
  private get label(): Phaser.GameObjects.Text { return this.visuals.label; }
  private get healthBar(): Phaser.GameObjects.Graphics { return this.visuals.healthBar; }
  private get shieldIcon(): Phaser.GameObjects.Graphics { return this.visuals.shieldIcon; }
  private get mysteryGlyph(): Phaser.GameObjects.Text | undefined { return this.visuals.mysteryGlyph; }
  private pointerHandler?: () => void;
  private readonly handlePointerDown = (): void => {
    if (!this.active) return;
    this.pointerHandler?.();
  };
  private readonly forwardVisiblePointerDown = (...args: unknown[]): void => {
    if (!this.active) return;
    if (this.pointerHandler) {
      this.pointerHandler();
      return;
    }
    this.emit(Phaser.Input.Events.POINTER_DOWN, ...args);
  };

  private refitLabel(): void {
    this.labelBaseScale = fitText(this.label, this.labelMaxW, this.labelMaxH);
    if (this.isCircuitVisual && this.kind !== "gold") {
      this.labelBaseScale = Math.max(this.labelBaseScale, CIRCUIT_LABEL_MIN_SCALE);
      this.label.setScale(this.labelBaseScale);
    }
  }

  private shouldShowOverlay(): boolean {
    if (this.compositeVisualTexture) return false;
    return this.kind !== "mystery" || this.isCircuitVisual;
  }

  private hideRichVisuals(): void {
    this.visuals.hide(FORCE_LOW_SPEC_RENDER_ENABLED);
  }

  private motionTweenTargets(): Phaser.GameObjects.GameObject[] {
    return this.visuals.targets(this).filter((target) => target !== this.pressureDanger);
  }

  /** 1회 초기화만 수행하고, per-spawn 상태는 configure가 매 acquire마다 전부 리셋한다. */
  constructor(scene: Phaser.Scene) {
    super(scene, 0, 0, DEFAULT_BRICK_TEX.base);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    (this.body as Phaser.Physics.Arcade.Body).setImmovable(true);
    (this.body as Phaser.Physics.Arcade.Body).enable = false; // configure의 enableBody가 켠다
    this.setDepth(DEPTH.brickBase);
    this.visuals = new BrickVisualParts(
      scene,
      DEFAULT_BRICK_TEX.base,
      DEFAULT_BRICK_TEX.overlay,
      MYSTERY_CHEST_ICON_KEY,
      this.forwardVisiblePointerDown,
    );
    this.setName("brickPhysicsProxy");
    this.on(Phaser.Input.Events.POINTER_DOWN, this.handlePointerDown);
    this.setActive(false).setVisible(false);
  }

  setPointerHandler(handler: () => void): void {
    this.pointerHandler = handler;
  }

  enableTargetInput(): void {
    this.disableTargetInput();
    const target = FORCE_LOW_SPEC_RENDER_ENABLED
      ? this
      : this.kind === "mystery" && !this.isCircuitVisual
        ? this.mysteryIcon
        : this.baseFrame;
    target.setInteractive({ useHandCursor: true });
  }

  disableTargetInput(): void {
    this.disableInteractive();
    this.baseFrame.disableInteractive();
    this.mysteryIcon.disableInteractive();
  }

  /** 전투 압박 판정용 안정 y. 피격 shake 중에도 변하지 않고, 실제 하강/펼침 offset에는 같이 갱신된다. */
  get pressureAnchorY(): number {
    return this.restY;
  }

  /** 전투 압박 패배 판정용 본체 그래픽 bottom. 물리 proxy가 아니라 실제 보이는 본체 기준이다. */
  get pressureVisualBottomY(): number {
    const anchor = !FORCE_LOW_SPEC_RENDER_ENABLED && this.mysteryIcon.visible
      ? this.mysteryIcon
      : !FORCE_LOW_SPEC_RENDER_ENABLED && this.baseFrame.visible
        ? this.baseFrame
        : this;
    return anchor.y + anchor.displayHeight / 2;
  }

  /**
   * 발사분(셀 1개)으로 재구성(신규/풀 재사용 공통) — 모든 per-spawn 상태를 리셋한다.
   * 텍스처(base/overlay/crack)·HP·tint·앵커·라벨·보조 글리프·바디를 전부 갱신.
   * BattlefieldRunner.spawnBrick이 직후 setInteractive + 마스크를 적용한다.
   */
  configure(x: number, y: number, cfg: BrickConfig): void {
    this.cancelVisualScaleTween();
    this.scene.tweens.killTweensOf(this.visuals.targets(this));
    this.visuals.addToDisplayList();
    this.cancelPressureDropTween();
    this.setPressureDangerTarget(false);
    this.sparkle.clear().setActive(false).setVisible(false);
    this.clearMarkStatus();
    this.mysteryIcon.setActive(false).setVisible(false);
    const tex = pickBrickTexture(cfg.kind, cfg.worldTextures);
    this.tex = tex;
    const visualState = resolveBrickSpawnVisualState(this.scene, tex, cfg);
    this.visualTex = visualState.visualTex;
    this.compositeVisualTexture = visualState.compositeVisualTexture;
    this.maxHp = cfg.hp;
    this.hp = cfg.hp;
    this.rewardGold = cfg.rewardGold;
    this.gridX = cfg.gridX;
    this.gridY = cfg.gridY;
    this.widthCells = Math.max(1, Math.floor(cfg.widthCells ?? 1));
    this.heightCells = Math.max(1, Math.floor(cfg.heightCells ?? 1));
    this.strength = cfg.strength;
    this.kind = cfg.kind;
    this.showHpLabel = cfg.showHpLabel !== false;
    this.dimGoldVisual = visualState.dimGoldVisual;
    this.isCircuitVisual = visualState.isCircuitVisual;
    this.noteId = cfg.noteId;
    // 특수 벽돌 상태 — 미지정이면 0/ZERO로 리셋(풀 재사용 시 이전 shield/armor/regen 잔존 방지).
    this.shieldHits = cfg.shieldHits ?? 0;
    this.maxShieldHits = cfg.shieldHits ?? 0;
    this.armor = cfg.armor ?? BigNumber.ZERO;
    this.healIntervalMs = cfg.healIntervalMs ?? 0;
    this.healAmountRatio = cfg.healAmountRatio ?? 0;
    this.healRadius = cfg.healRadius ?? 0;
    // regen은 직전 회복부터 한 주기 뒤 첫 회복(생성 즉시 회복 방지). 비-regen은 미사용.
    this.lastHealMs = this.scene.time.now;
    this.shatterVariant = pickShatterVariant(cfg.gridX, cfg.gridY, cfg.shatterSalt ?? 0);
    this.lastHitMs = 0;
    this.comboCount = 0;
    this.crackStage = 0;
    this.sparklePhase = ((cfg.gridX * 37 + cfg.gridY * 19 + (cfg.shatterSalt ?? 0)) % 100) / 100;
    this.lastSparkleDrawMs = Number.NEGATIVE_INFINITY;
    this.restX = x;
    this.restY = y;
    const isMystery = cfg.kind === "mystery";
    const enemyScale = normalizeVisualScale(cfg.enemyScale);
    this.enemyVisualScale = enemyScale;
    this.unscaledDisplayWidth = cfg.width / enemyScale;
    this.unscaledDisplayHeight = cfg.height / enemyScale;
    const visualWidth = visualState.displayWidth;
    const visualHeight = visualState.displayHeight;
    this.unscaledVisualDisplayWidth = visualWidth / enemyScale;
    this.unscaledVisualDisplayHeight = visualHeight / enemyScale;
    this.visualDisplayWidth = visualWidth;
    this.visualDisplayHeight = visualHeight;
    const showCircuitMysteryBody = isMystery && this.isCircuitVisual;
    this.labelRestY = brickLabelRestY(y, cfg.height, isMystery);
    this.glyphRestY = brickGlyphRestY(y, cfg.height);
    // 생성 좌표는 layoutBrickCells가 `- contentOffset`을 이미 적용한 값 → base는 그만큼 되돌린 위치.
    this.contentBaseY = deriveContentBaseY(
      { y, restY: y, labelRestY: this.labelRestY, glyphRestY: this.glyphRestY },
      cfg.contentOffset ?? 0,
    );
    // base — 텍스처/위치/크기 + 바디 재활성. setDisplaySize가 scale을 설정하므로 setScale 금지.
    this.setTexture(FORCE_LOW_SPEC_RENDER_ENABLED ? this.visualTex.base : tex.base).setDisplaySize(cfg.width, cfg.height);
    this.enableBody(true, x, y, true, true);
    (this.body as Phaser.Physics.Arcade.Body).setImmovable(true);
    this.setAlpha(FORCE_LOW_SPEC_RENDER_ENABLED ? 1 : 0).clearMask();

    const baseFrameTexture = this.compositeVisualTexture ?? this.visualTex.base;
    const showOverlayBody = !this.compositeVisualTexture && (!isMystery || showCircuitMysteryBody);

    this.baseFrame
      .setTexture(baseFrameTexture)
      .setPosition(x, y)
      .setDisplaySize(visualWidth, visualHeight)
      .setActive(!isMystery || showCircuitMysteryBody)
      .setVisible(!isMystery || showCircuitMysteryBody)
      .setAlpha(this.dimGoldVisual ? 0 : 1)
      .clearMask();

    this.overlay
      .setTexture(this.visualTex.overlay)
      .setPosition(x, y)
      .setDisplaySize(visualWidth, visualHeight)
      .setActive(showOverlayBody)
      .setVisible(showOverlayBody)
      .setAlpha(this.dimGoldVisual ? 0 : 1)
      .clearMask();

    this.crack
      .setTexture(crackTextureFor(this.shatterVariant, 1))
      .setPosition(x, y)
      .setDisplaySize(visualWidth, visualHeight)
      .setActive(false)
      .setVisible(false)
      .clearMask();

    this.updateShieldPresentationLayout(cfg.width, cfg.height);
    if (this.isCircuitVisual && cfg.kind !== "gold") {
      this.labelMaxW = Math.min(visualWidth * 0.56, cfg.width * 0.76);
      this.labelMaxH = Math.min(visualHeight * 0.38, cfg.height * 0.58);
    }
    if (cfg.kind === "gold" && visualState.scale < 1) {
      this.labelMaxW = Math.min(this.labelMaxW, visualWidth * 0.64);
      this.labelMaxH = Math.min(this.labelMaxH, visualHeight * 0.5);
    }
    this.label
      .setText(this.labelText())
      .setPosition(this.labelRestX, this.labelRestY)
      .setScale(1)
      .setAlpha(this.dimGoldVisual ? 0.68 : isMystery ? 0.76 : 1)
      .setActive(this.hpLabelVisible())
      .setVisible(this.hpLabelVisible())
      .clearMask();
    this.applyLabelTextStyle();
    this.refitLabel();
    this.drawHealthBar(x, y, visualWidth, visualHeight);

    if (this.mysteryGlyph) {
      this.scene.tweens.killTweensOf(this.mysteryGlyph);
      this.mysteryGlyph.setActive(false).setVisible(false);
    }

    // base + 룬 overlay 둘 다 tint. mystery는 base/overlay를 숨기고 PNG chest만 표시한다.
    this.applyTint();
    this.setActive(true).setVisible(!isMystery);
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      const selfDisplay = this as Phaser.GameObjects.GameObject & {
        displayList?: unknown;
        addToDisplayList?: () => unknown;
      };
      if (!selfDisplay.displayList) selfDisplay.addToDisplayList?.();
      this.setActive(true).setVisible(true).setAlpha(1);
      this.hideRichVisuals();
    } else {
      // 실제 그래픽은 baseFrame/overlay/label이 담당한다. 물리 proxy sprite는 충돌·조준 좌표만 유지하고
      // display list에서는 제거해 WebView/Phaser 렌더 순회 비용을 줄인다.
      this.setVisible(false);
      this.removeFromDisplayList();
    }
    this.visuals.syncDisplayList();
    this.enableTargetInput();
  }

  applyEnemyScale(enemyScale: number, animate = true): void {
    const nextScale = normalizeVisualScale(enemyScale);
    if (Math.abs(this.enemyVisualScale - nextScale) < 0.001) return;
    this.enemyVisualScale = nextScale;
    const metrics = {
      width: this.unscaledDisplayWidth * nextScale,
      height: this.unscaledDisplayHeight * nextScale,
      visualWidth: this.unscaledVisualDisplayWidth * nextScale,
      visualHeight: this.unscaledVisualDisplayHeight * nextScale,
    };
    if (!animate) {
      this.applyDisplayMetrics(metrics);
      return;
    }
    this.cancelVisualScaleTween();
    const tweenTarget = {
      width: this.displayWidth,
      height: this.displayHeight,
      visualWidth: this.baseFrame.visible ? this.baseFrame.displayWidth : this.visualDisplayWidth,
      visualHeight: this.baseFrame.visible ? this.baseFrame.displayHeight : this.visualDisplayHeight,
    };
    this.visualScaleTweenTarget = tweenTarget;
    this.scene.tweens.add({
      targets: tweenTarget,
      ...metrics,
      duration: ENEMY_SCALE_TWEEN_MS,
      ease: "Sine.easeInOut",
      onUpdate: () => this.applyDisplayMetrics(tweenTarget),
      onComplete: () => {
        this.applyDisplayMetrics(metrics);
        if (this.visualScaleTweenTarget === tweenTarget) this.visualScaleTweenTarget = undefined;
      },
    });
  }

  /** 소멸 처리 — 비활성+숨김+바디 off + 모든 child/tween 정리. 풀 반납(onRelease)이 호출. destroy 아님(재사용). */
  deactivate(): void {
    this.cancelVisualScaleTween();
    this.scene.tweens.killTweensOf(this.visuals.targets(this));
    this.cancelPressureDropTween();
    this.setPressureDangerTarget(false);
    this.disableTargetInput();
    this.disableBody(true, true); // body.stop + enable=false + setActive(false) + setVisible(false)
    this.compositeVisualTexture = undefined;
    this.clearMarkStatus();
    this.visuals.hide(true);
  }

  /** 만피 여부. */
  private isFullHp(): boolean {
    return this.hp.eq(this.maxHp);
  }

  /** HP 라벨 표시 여부 — showHpLabel 설정 + 만피가 아닐 때만 표시. */
  private hpLabelVisible(): boolean {
    return shouldShowBrickHpLabel(this.showHpLabel, this.isFullHp(), this.hasActiveShield());
  }

  /** 라벨 표시 문자열 — shield는 방어막 횟수, 그 외(소진 포함)는 HP 수치. */
  private labelText(): string {
    return resolveBrickLabelText(this.kind, formatBig(this.hp), this.shieldHits);
  }

  private applyLabelTextStyle(): void {
    applyBrickLabelTextStyle(this.label, this.kind, this.isCircuitVisual, this.dimGoldVisual);
  }

  private updateHpLabelPresentation(): void {
    this.updateShieldPresentationLayout(); this.label.setText(this.labelText()).setPosition(this.labelRestX, this.labelRestY); this.refitLabel();
    this.label.setActive(this.hpLabelVisible()).setVisible(this.hpLabelVisible());
    this.drawHealthBar();
  }

  private drawHealthBar(
    x = this.restX,
    y = this.restY,
    displayWidth = this.baseFrame.visible ? this.baseFrame.displayWidth : this.displayWidth,
    displayHeight = this.baseFrame.visible ? this.baseFrame.displayHeight : this.displayHeight,
  ): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED || this.showHpLabel || this.isFullHp()) {
      this.healthBar.clear().setActive(false).setVisible(false);
      return;
    }
    drawBrickHealthBar({ healthBar: this.healthBar, hp: this.hp, maxHp: this.maxHp, x, y, displayWidth, displayHeight });
  }

  private punchHpLabel(): void {
    if (!this.showHpLabel) return;
    this.label.setScale(this.labelBaseScale * 1.3);
    this.scene.tweens.add({ targets: this.label, scale: this.labelBaseScale, duration: 90, ease: "Quad.easeOut" });
  }

  private refreshMovingVisuals(): void {
    this.drawKindDetail(); this.drawSparkle(this.scene.time.now); this.drawShieldIcon(); this.drawGlow(this.tintTopLeft);
    this.drawMarkStatus(); this.syncAttachedVisualPositions();
  }

  private hasActiveShield(): boolean {
    return this.kind === "shield" && this.shieldHits > 0;
  }

  private updateShieldPresentationLayout(width = this.displayWidth, height = this.displayHeight): void {
    const layout = resolveShieldBadgeLayout(width, height, this.hasActiveShield());
    this.labelMaxW = layout.labelMaxWidth;
    this.labelMaxH = height * 0.9;
    if (this.isCircuitVisual && this.kind !== "gold") {
      this.labelMaxW = Math.min(this.labelMaxW, width * 0.76);
      this.labelMaxH = Math.min(this.labelMaxH, height * 0.58);
    }
    this.labelRestX = this.restX + layout.labelOffsetX;
    this.shieldIconRestX = this.restX + layout.iconOffsetX;
    this.shieldIconSize = layout.iconSize;
    this.drawShieldIcon();
  }

  private drawShieldIcon(): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.shieldIcon.clear().setActive(false).setVisible(false);
      return;
    }
    this.shieldIcon.clear();
    if (!this.hasActiveShield() || this.shieldIconSize <= 0) {
      this.shieldIcon.setActive(false).setVisible(false);
      return;
    }
    const s = this.shieldIconSize;
    const half = s / 2;
    this.shieldIcon
      .setPosition(this.shieldIconRestX, this.labelRestY)
      .lineStyle(Math.max(1, s * 0.09), BRICK_DETAIL_COLOR, 0.96)
      .fillStyle(BRICK_DETAIL_COLOR, 0.22)
      .beginPath()
      .moveTo(0, -half)
      .lineTo(half * 0.72, -half * 0.52)
      .lineTo(half * 0.56, half * 0.28)
      .lineTo(0, half)
      .lineTo(-half * 0.56, half * 0.28)
      .lineTo(-half * 0.72, -half * 0.52)
      .closePath()
      .fillPath()
      .strokePath()
      .setActive(true)
      .setVisible(true);
  }

  /**
   * kind별 tint 적용 — fixed(gold/mystery/shield·armor·regen)는 고정색, strength는 강도+남은HP 무지개.
   * 단 shield는 방어막 소진(shieldHits≤0) 시 일반 무지개로 되돌려 "방어막 깨짐"을 시각화한다(기획 §방어막 비주얼).
   */
  private applyTint(): void {
    let tint: number;
    if (this.kind === "shield" && this.shieldHits <= 0) {
      tint = strengthTint(this.strength, this.hp, this.maxHp); // 방어막 소진 → 일반 벽돌색
    } else if (this.tex.tintMode === "fixed") {
      tint = this.tex.fixedTint!;
    } else {
      tint = strengthTint(this.strength, this.hp, this.maxHp);
    }
    this.setTint(tint);
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.hideRichVisuals();
      return;
    }
    if (this.compositeVisualTexture) {
      this.baseFrame.clearTint();
    } else if (this.tex.tintBase) {
      this.baseFrame.setTint(tint);
    } else {
      this.baseFrame.clearTint();
    }
    if (this.tex.tintOverlay) {
      this.overlay.setTint(tint);
    } else {
      this.overlay.clearTint();
    }
    const showOverlay = this.shouldShowOverlay();
    this.overlay.setActive(showOverlay).setVisible(showOverlay);
    this.drawGlow(tint);
    this.drawKindDetail();
    this.drawSparkle(this.scene.time.now);
  }

  private drawGlow(tint: number): void {
    void tint;
    this.glow.clear();
    this.glow.setActive(false).setVisible(false);
    this.glow.removeFromDisplayList();
    this.glowImage.setActive(false).setVisible(false);
  }

  setPressureDangerTarget(active: boolean): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.pressureDangerTarget = false;
      this.pressureDanger.clear().setAlpha(1).setActive(false).setVisible(false);
      return;
    }
    if (this.pressureDangerTarget === active) {
      if (active) {
        this.drawPressureDanger();
      } else if (this.pressureDanger.active || this.pressureDanger.visible) {
        this.scene.tweens.killTweensOf(this.pressureDanger);
        this.pressureDanger.clear().setAlpha(1).setActive(false).setVisible(false);
      }
      return;
    }
    this.pressureDangerTarget = active;
    this.scene.tweens.killTweensOf(this.pressureDanger);
    if (!active) {
      this.pressureDanger.clear().setAlpha(1).setActive(false).setVisible(false);
      return;
    }
    this.drawPressureDanger();
    this.pressureDanger.setAlpha(PRESSURE_DANGER_ALPHA).setActive(true).setVisible(true);
    this.scene.tweens.add({
      targets: this.pressureDanger,
      alpha: PRESSURE_DANGER_MIN_ALPHA,
      duration: PRESSURE_DANGER_BLINK_MS,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });
  }

  private drawPressureDanger(): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) return;
    drawBrickPressureDanger({
      kind: this.kind,
      x: this.x,
      y: this.y,
      displayWidth: this.displayWidth,
      displayHeight: this.displayHeight,
      baseFrameDisplayWidth: this.baseFrame.displayWidth,
      baseFrameDisplayHeight: this.baseFrame.displayHeight,
      pressureDanger: this.pressureDanger,
    });
  }

  private drawKindDetail(): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.kindDetail.clear().setActive(false).setVisible(false);
      this.mysteryIcon.setActive(false).setVisible(false);
      return;
    }
    drawBrickKindDetail({
      kind: this.kind,
      tex: this.tex,
      isCircuitVisual: this.isCircuitVisual,
      dimGoldVisual: this.dimGoldVisual,
      widthCells: this.widthCells,
      heightCells: this.heightCells,
      x: this.x,
      y: this.y,
      displayWidth: this.visualDisplayWidth,
      displayHeight: this.visualDisplayHeight,
      kindDetail: this.kindDetail,
      mysteryIcon: this.mysteryIcon,
    });
  }

  private drawSparkle(timeMs: number): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.sparkle.clear().setActive(false).setVisible(false);
      return;
    }
    this.lastSparkleDrawMs = drawBrickSparkle({
      kind: this.kind,
      isCircuitVisual: this.isCircuitVisual,
      x: this.x,
      y: this.y,
      displayWidth: this.displayWidth,
      displayHeight: this.displayHeight,
      sparklePhase: this.sparklePhase,
      timeMs,
      lastDrawMs: this.lastSparkleDrawMs,
      sparkle: this.sparkle,
    });
  }

  private drawMarkStatus(
    x = this.x,
    y = this.y,
    displayWidth = this.displayWidth,
    displayHeight = this.displayHeight,
  ): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.markStatus.hideVisuals();
      return;
    }
    this.markStatus.draw({
      isMarked: this.isMarked,
      kind: this.kind,
      x,
      y,
      displayWidth,
      displayHeight,
    });
  }

  get isMarked(): boolean {
    return this.markStatus.isMarked;
  }

  get markDamageMultiplier(): number {
    return this.markStatus.damageMultiplier;
  }

  applyMarkStatus(damageMultiplier: number, hits: number): void {
    this.markStatus.apply({
      isMarked: this.isMarked,
      kind: this.kind,
      x: this.x,
      y: this.y,
      displayWidth: this.displayWidth,
      displayHeight: this.displayHeight,
    }, damageMultiplier, hits);
    if (FORCE_LOW_SPEC_RENDER_ENABLED) this.markStatus.hideVisuals();
  }

  consumeMarkHit(): void {
    this.markStatus.consume({
      isMarked: this.isMarked,
      kind: this.kind,
      x: this.x,
      y: this.y,
      displayWidth: this.displayWidth,
      displayHeight: this.displayHeight,
    });
    if (FORCE_LOW_SPEC_RENDER_ENABLED) this.markStatus.hideVisuals();
  }

  /**
   * 방어막 1 소모(피해량 무관) — damageBrick이 resolveBrickDamage.blockedByShield일 때 호출. shieldHits를
   * 줄이고 뱃지(라벨)를 갱신한다. 0 도달 시 라벨을 HP로 전환 + tint를 일반 벽돌색으로(방어막 깨짐 시각화).
   */
  consumeShield(): void {
    if (this.shieldHits > 0) this.shieldHits -= 1;
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.applyTint();
      return;
    }
    this.updateHpLabelPresentation();
    this.applyTint();
    this.syncAttachedVisualPositions();
    this.punchHpLabel();
  }

  /**
   * 재생(regen) 회복 적용 — update 틱이 주변 regen 벽돌로부터 계산한 newHp(상한 maxHp 클램프 완료)를 받아
   * HP·라벨·tint·균열을 갱신한다. shield 방어막 생존 중인 벽돌은 회복 대상에서 제외되므로 라벨은 HP로 표시.
   */
  applyHeal(newHp: BigNumber): void {
    this.hp = newHp;
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.applyTint();
      return;
    }
    this.updateHpLabelPresentation();
    this.applyTint();
    this.updateCrack(); // 회복으로 HP 비율↑ → 균열 단계 하향/숨김
    this.syncAttachedVisualPositions();
  }

  /** 피해 적용. HP가 0 이하가 되면 true(파괴) 반환 — 호출자가 destroy + 보상 처리. */
  takeDamage(amount: BigNumber): boolean {
    this.hp = this.hp.sub(amount);
    if (this.hp.lte(BigNumber.ZERO)) return true;
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.applyTint();
      return false;
    }
    this.updateHpLabelPresentation(); // HP 자릿수 변화 → 재fit(블록 안 유지)
    this.applyTint();
    this.updateCrack();
    this.drawMarkStatus();
    this.syncAttachedVisualPositions();
    // 숫자 감소 튐 — 피해가 읽히는 펀치 스케일(fit 기준 scale에 곱). handleHit이 playImpactShake(killTweensOf 포함)
    // 후에 takeDamage를 호출하므로 이 트윈은 살아남는다 (역순 호출 시 kill됨 — 순서 의존 주의).
    this.punchHpLabel();
    return false;
  }

  /** 남은 HP 비율 → 균열 단계 전환 (0=숨김, 1~4=variant 패턴 손상 누적) — takeDamage마다 호출. */
  private updateCrack(): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.crack.setActive(false).setVisible(false);
      this.crackStage = 0;
      return;
    }
    if (this.kind === "mystery") {
      this.crack.setActive(false).setVisible(false);
      this.crackStage = 0;
      return;
    }
    const hpRatio = this.maxHp.isZero() ? 0 : this.hp.div(this.maxHp).toNumber();
    const stage = crackStageFor(hpRatio);
    if (stage === this.crackStage) return;
    this.crackStage = stage;
    if (stage === 0) {
      this.crack.setActive(false).setVisible(false);
      return;
    }
    this.crack
      .setTexture(crackTextureFor(this.shatterVariant, stage))
      .setDisplaySize(
        this.baseFrame.visible ? this.baseFrame.displayWidth : this.displayWidth,
        this.baseFrame.visible ? this.baseFrame.displayHeight : this.displayHeight,
      )
      .setActive(true)
      .setVisible(true);
  }

  /**
   * 파괴 시 조각 분해 깊이 — "죽는 순간 표시되던 균열 단계"(0=원샷~4=severe).
   * takeDamage는 hp≤0이면 updateCrack 없이 즉시 true 반환하므로 crackStage는 마지막 비파괴 타격 단계가
   * 보존된다 = 사용자가 보던 금. 약한 블록(원샷 0)은 큰 덩어리, 깊게 금 간 블록은 잘게 쪼개진다.
   */
  get destroyShatterStage(): number {
    return this.crackStage;
  }

  /** HP 라벨과 보조 표식을 실제 보이는 본체 좌표에 동기화한다. */
  private syncAttachedVisualPositions(time: number = this.scene.time.now): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) return;
    const anchor = this.baseFrame.visible ? this.baseFrame : this;
    const dx = anchor.x - this.restX;
    const dy = anchor.y - this.restY;
    this.label.setPosition(this.labelRestX + dx, this.labelRestY + dy);
    if (this.baseFrame.visible && (this.baseFrame.x !== this.x || this.baseFrame.y !== this.y)) {
      this.baseFrame.setPosition(anchor.x, anchor.y);
    }
    if (this.hasActiveShield()) this.shieldIcon.setPosition(this.shieldIconRestX + dx, this.labelRestY + dy);
    if (this.kindDetail.visible) this.kindDetail.setPosition(anchor.x, anchor.y);
    if (this.mysteryIcon.visible) this.mysteryIcon.setPosition(anchor.x, anchor.y);
    if (this.markStatus.visible) this.drawMarkStatus(anchor.x, anchor.y, anchor.displayWidth, anchor.displayHeight);
    if (this.healthBar.visible) this.drawHealthBar(anchor.x, anchor.y, anchor.displayWidth, anchor.displayHeight);
    this.mysteryGlyph?.setPosition(this.restX + dx, this.glyphRestY + dy);
    if (this.kind === "mystery") this.drawSparkle(time);
    if (dx !== 0 || dy !== 0) this.drawGlow(this.tintTopLeft);
    if (this.pressureDangerTarget) this.drawPressureDanger();
    this.visuals.syncDisplayList();
  }

  private applyDisplayMetrics(metrics: {
    readonly width: number;
    readonly height: number;
    readonly visualWidth: number;
    readonly visualHeight: number;
  }): void {
    this.setDisplaySize(metrics.width, metrics.height);
    const body = this.body as Phaser.Physics.Arcade.Body | null;
    body?.setSize(metrics.width, metrics.height, true);
    this.visualDisplayWidth = metrics.visualWidth;
    this.visualDisplayHeight = metrics.visualHeight;
    this.baseFrame.setDisplaySize(metrics.visualWidth, metrics.visualHeight);
    this.overlay.setDisplaySize(metrics.visualWidth, metrics.visualHeight);
    this.crack.setDisplaySize(metrics.visualWidth, metrics.visualHeight);
    this.labelRestY = brickLabelRestY(this.restY, metrics.height, this.kind === "mystery");
    this.glyphRestY = brickGlyphRestY(this.restY, metrics.height);
    this.updateShieldPresentationLayout(metrics.width, metrics.height);
    this.label.setPosition(this.labelRestX, this.labelRestY);
    this.refitLabel();
    this.refreshMovingVisuals();
  }

  private cancelVisualScaleTween(): void {
    if (!this.visualScaleTweenTarget) return;
    this.scene.tweens.killTweensOf(this.visualScaleTweenTarget);
    this.visualScaleTweenTarget = undefined;
  }

  /**
   * 매 프레임 HP 라벨·"?" 글리프를 실제 본체 위치에 동기화 — 피격 shake가 baseFrame을 흔들 때
   * 라벨/글리프도 한 몸으로 따라가 "따로 노는" 어긋남을 없앤다. 글리프 pulse(scale)는 독립 tween이라 영향 없음.
   */
  override preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    this.syncAttachedVisualPositions(time);
  }

  /**
   * 피격 반동 흔들림 — dir 방향으로 4단계 chain tween. scale은 흔들림 세기 배수(기본 1):
   * 비크리티컬은 <1(약하게), 크리티컬은 >1(세게)로 호출부(BattlefieldRunner)에서 데이터 주입(global.tsv).
   */
  playImpactShake(dirX: number, dirY: number, scale = 1): void {
    if (FORCE_LOW_SPEC_RENDER_ENABLED) {
      this.scene.tweens.killTweensOf(this);
      this.setPosition(this.restX, this.restY);
      return;
    }
    const len = Math.hypot(dirX, dirY);
    const x = len > 0 ? dirX / len : 0;
    const y = len > 0 ? dirY / len : -1;
    const step1 = 5 * scale;
    const step2 = 2.5 * scale;
    // 미스터리 글리프는 pulse tween 유지를 위해 kill 대상에서 제외(흔들림·펀치 대상도 아님).
    // 흔들림은 base/overlay/crack만 — HP 라벨·"?" 글리프는 분리 오프셋 고정(흔들면 오프셋이 중앙으로 끌려가 다시 겹침).
    const shakeTargets = [this, this.baseFrame, this.overlay, this.crack, this.mysteryIcon];

    this.scene.tweens.killTweensOf(this.motionTweenTargets());
    this.setPosition(this.restX, this.restY);
    this.baseFrame.setPosition(this.restX, this.restY);
    this.overlay.setPosition(this.restX, this.restY);
    this.crack.setPosition(this.restX, this.restY);
    this.mysteryIcon.setPosition(this.restX, this.restY);
    this.drawGlow(this.tintTopLeft);
    this.drawSparkle(this.scene.time.now);
    this.drawMarkStatus();
    this.syncAttachedVisualPositions(this.scene.time.now);
    const syncDuringMotion = (): void => this.syncAttachedVisualPositions(this.scene.time.now);
    // 라벨/글리프 위치는 preUpdate가 base에 동기화 — 여기서 리셋 불필요(base shake를 한 몸으로 따라감).
    this.scene.tweens.chain({
      targets: shakeTargets,
      tweens: [
        { x: this.restX + x * step1, y: this.restY + y * step1, duration: 34, ease: "Quad.easeOut", onUpdate: syncDuringMotion },
        { x: this.restX - x * step2, y: this.restY - y * step2, duration: 48, ease: "Sine.easeOut", onUpdate: syncDuringMotion },
        { x: this.restX + x * 1.1 * scale, y: this.restY + y * 1.1 * scale, duration: 42, ease: "Sine.easeOut", onUpdate: syncDuringMotion },
        { x: this.restX, y: this.restY, duration: 58, ease: "Quad.easeOut", onUpdate: syncDuringMotion },
      ],
      onComplete: () => {
        this.drawGlow(this.tintTopLeft);
        this.drawKindDetail();
        this.drawSparkle(this.scene.time.now);
        this.drawShieldIcon();
        this.drawMarkStatus();
        this.syncAttachedVisualPositions(this.scene.time.now);
      },
    });
  }

  /**
   * 게임뷰 콘텐츠 펼치기 오프셋 평행이동 — 시각 y + rest 앵커(restY/labelRestY/glyphRestY) + overlay/crack을
   * delta만큼 위로(빼기) 옮긴다. ⚠ rest 앵커를 함께 옮겨야 이후 피격 shake(playImpactShake가 restY로 복귀)가
   * 옮겨진 위치로 돌아간다 — 안 그러면 첫 피격에 base로 되돌아가 발사대 아래로 떨어진다(BattlefieldRunner.applyGameviewOffset).
   * 라벨/글리프는 preUpdate가 restY 기준으로 매 프레임 동기하므로 여기선 앵커만 옮기면 자동 정합.
   */
  shiftContentOffset(delta: number): void {
    if (delta === 0) return;
    const a = shiftBrickAnchorsY(
      { y: this.y, restY: this.restY, labelRestY: this.labelRestY, glyphRestY: this.glyphRestY },
      delta,
    );
    this.setY(a.y);
    this.baseFrame.y -= delta;
    this.restY = a.restY;
    this.labelRestY = a.labelRestY;
    this.glyphRestY = a.glyphRestY;
    // overlay/crack은 별도 restY가 없고 생성 시 base와 동일 y(= restY)다. delta 직접 이동으로 그 불변식
    // (overlay.y === crack.y === restY)을 유지 — playImpactShake가 restY로 복귀시켜도 어긋나지 않는다.
    this.overlay.y -= delta;
    this.crack.y -= delta;
    this.refreshMovingVisuals();
  }

  /** 전투 시간 압박으로 벽돌 자체를 이동한다. offset reconcile에 되돌아가지 않도록 base 앵커도 같이 이동한다. */
  translateContentY(deltaY: number): void {
    if (deltaY === 0) return;
    const a = translateBrickAnchorsY(
      { y: this.y, restY: this.restY, labelRestY: this.labelRestY, glyphRestY: this.glyphRestY },
      deltaY,
    );
    this.contentBaseY = translateBrickAnchorsY(this.contentBaseY, deltaY);
    this.setY(a.y);
    this.baseFrame.y += deltaY;
    this.restY = a.restY;
    this.labelRestY = a.labelRestY;
    this.glyphRestY = a.glyphRestY;
    this.overlay.y += deltaY;
    this.crack.y += deltaY;
    this.refreshMovingVisuals();
  }

  translateContentX(deltaX: number): void {
    if (deltaX === 0) return;
    this.restX += deltaX;
    this.labelRestX += deltaX;
    this.shieldIconRestX += deltaX;
    this.setX(this.x + deltaX);
    for (const target of [this.baseFrame, this.overlay, this.crack, this.mysteryIcon]) target.x += deltaX;
    this.refreshMovingVisuals();
  }

  translateContent(deltaX: number, deltaY: number): void { this.translateContentX(deltaX); this.translateContentY(deltaY); }

  /** 전투 시간 압박 하강 연출 — 실제 앵커를 조금씩 내리면서 좌우로 덜그덕 흔든다. */
  playPressureDrop(deltaY: number): void {
    if (deltaY === 0) return;
    const amount = Math.max(0, deltaY);
    if (amount <= 0) return;

    this.cancelPressureDropTween();
    this.scene.tweens.killTweensOf(this.visuals.targets(this));

    const progress = { y: 0 };
    const token = ++this.pressureDropTweenToken;
    this.pressureDropTweenTarget = progress;
    let applied = 0;
    this.scene.tweens.add({
      targets: progress,
      y: amount,
      duration: PRESSURE_DROP_DURATION_MS,
      ease: "Cubic.easeInOut",
      onUpdate: () => {
        if (token !== this.pressureDropTweenToken) return;
        const delta = progress.y - applied;
        applied = progress.y;
        this.translateContentY(delta);
      },
      onComplete: () => {
        if (token !== this.pressureDropTweenToken) return;
        const remainder = amount - applied;
        if (remainder !== 0) this.translateContentY(remainder);
        this.pressureDropTweenTarget = undefined;
      },
    });

    this.scene.tweens.chain({
      targets: [this, this.baseFrame, this.overlay, this.crack],
      tweens: [
        { x: this.restX - PRESSURE_DROP_JITTER_PX, duration: 42, ease: "Quad.easeOut", onUpdate: () => this.syncAttachedVisualPositions() },
        { x: this.restX + PRESSURE_DROP_JITTER_PX, duration: 54, ease: "Quad.easeInOut", onUpdate: () => this.syncAttachedVisualPositions() },
        { x: this.restX - PRESSURE_DROP_JITTER_PX * 0.5, duration: 48, ease: "Quad.easeInOut", onUpdate: () => this.syncAttachedVisualPositions() },
        { x: this.restX, duration: 72, ease: "Back.easeOut", onUpdate: () => this.syncAttachedVisualPositions() },
      ],
    });
  }

  playPressureAdvance(deltaX: number, deltaY: number): void {
    if (deltaX === 0) { this.playPressureDrop(deltaY); return; }
    this.cancelPressureDropTween();
    this.scene.tweens.killTweensOf(this.visuals.targets(this));
    this.translateContent(deltaX, deltaY);
  }

  private cancelPressureDropTween(): void {
    this.pressureDropTweenToken++;
    if (!this.pressureDropTweenTarget) return;
    this.scene?.tweens?.killTweensOf(this.pressureDropTweenTarget);
    this.pressureDropTweenTarget = undefined;
  }

  /**
   * 게임뷰 콘텐츠 오프셋을 **절대** 적용 — base(offset 0 기준)에서 `y = base - offset`로 모든 앵커/파트를
   * 재설정한다. 누적(shiftContentOffset)과 달리 멱등이라, 펼침/접힘 동기가 race로 어긋나 벽돌이 화면 밖에
   * 잔존해도 다음 reconcile에 올바른 위치로 강제 복구된다. 마스크(시각)는 호출자가 별도 정합 — 여기선
   * 위치만(clamp 없이) 맞춘다(충돌은 실제 좌표, 시각만 마스크가 클립).
   */
  applyContentOffsetAbsolute(offset: number): void {
    const a = resolveAnchorsAtOffset(this.contentBaseY, offset);
    this.setY(a.y);
    this.baseFrame.y = a.restY;
    this.restY = a.restY;
    this.labelRestY = a.labelRestY;
    this.glyphRestY = a.glyphRestY;
    // 불변식 overlay.y === crack.y === restY 유지(playImpactShake 복귀 정합).
    this.overlay.y = a.restY;
    this.crack.y = a.restY;
    this.drawKindDetail();
    this.drawSparkle(this.scene.time.now);
    this.drawShieldIcon();
    this.drawGlow(this.tintTopLeft);
    this.drawMarkStatus();
    this.syncAttachedVisualPositions();
  }

  /** 현재 위치가 주어진 offset 기준 기대 위치와 어긋났는지(reconcile 필요 여부) — base 기준 판정. */
  isContentOffsetStale(offset: number, eps = 0.5): boolean {
    return contentOffsetMismatch(this.y, this.contentBaseY.y, offset, eps);
  }

  /**
   * 게임뷰 마스크를 벽돌의 모든 시각 파트(base + overlay + crack + HP 라벨 + "?" 글리프)에 적용한다.
   * Sprite(base)만 마스킹하면 별도 GameObject인 HP 라벨/룬/균열/글리프가 펼침 시 게임뷰 밖으로 삐져나온다
   * (경계에 걸친 벽돌의 체력 텍스트가 탭 영역 위에 보이는 문제, 2026-06-14).
   * RectMask.apply(단일 GO 추상)로는 내부 4파트에 개별 적용 불가라 Brick이 자기 파트를 직접 마스킹한다
   * (발사체 등 단일 GO는 RectMask.apply 그대로 사용 — 다중 파트 GO만 이 메서드).
   */
  applyContentMask(mask: Phaser.Display.Masks.GeometryMask): void {
    this.setMask(mask);
    this.visuals.applyMask(mask);
  }

  /**
   * 콘텐츠 마스크 해제 — 게임뷰 마스크 자체가 없을 때만 사용한다. 일반 전투 압박으로 벽돌이 아래로 내려와
   * 게임뷰 하단을 넘을 수 있으므로, 접힘 상태라도 active 벽돌은 기본적으로 마스크를 유지한다.
   */
  clearContentMask(): void {
    this.clearMask();
    this.visuals.clearMask();
  }

  override destroy(fromScene?: boolean): void {
    // 씬/게임 파괴 중에는 scene 참조가 이미 해제됨(UpdateList.shutdown 경유 destroy) — deref 방어.
    this.cancelPressureDropTween();
    this.visuals.destroy(this);
    super.destroy(fromScene);
  }
}
